import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'antd/dist/reset.css';
import { AuthProvider } from './context/AuthContext/AuthContext';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import PublicLayout from './layouts/PublicLayout/PublicLayout';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import './App.css';

import Home from './pages/Home/Home';

import About from './pages/About/About';
import Services from './pages/Services/Services';
import Pricing from './pages/Pricing/Pricing';
import News from './pages/News/News';
import NewsDetail from './pages/News/NewsDetail';
import Contact from './pages/Contact/Contact';
import Careers from './pages/Careers/Careers';
import Downloads from './pages/Downloads/Downloads';
import Credentials from './pages/Credentials/Credentials';
import NotFound from './pages/NotFound/NotFound';

// Admin 页面懒加载
const AdminLayout = lazy(() => import('./layouts/AdminLayout/AdminLayout'));
const LoginPage = lazy(() => import('./admin/LoginPage/LoginPage'));
const DashboardPage = lazy(() => import('./admin/DashboardPage/DashboardPage'));
const ContactsPage = lazy(() => import('./admin/ContactsPage/ContactsPage'));
const NewsListPage = lazy(() => import('./admin/NewsListPage/NewsListPage'));
const NewsFormPage = lazy(() => import('./admin/NewsFormPage/NewsFormPage'));
const UsersPage = lazy(() => import('./admin/UsersPage/UsersPage'));
const QuotationsPage = lazy(() => import('./admin/QuotationsPage/QuotationsPage'));
const AdminCareersPage = lazy(() => import('./admin/CareersPage/CareersPage'));
const CareersFormPage = lazy(() => import('./admin/CareersFormPage/CareersFormPage'));
const AboutManagePage = lazy(() => import('./admin/AboutPage/AboutPage'));
const SummitFormPage = lazy(() => import('./admin/SummitFormPage/SummitFormPage'));
const DatabasePage = lazy(() => import('./admin/DatabasePage/DatabasePage'));

const S = ({ children }) => <Suspense fallback={null}>{children}</Suspense>;

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* 公共页面：带 Navbar + Footer */}
          <Route element={<PublicLayout />}>
            <Route path='/' element={<Home />} />
            <Route path='/about' element={<About />} />
            <Route path='/services' element={<Services />} />
            <Route path='/pricing' element={<Pricing />} />
            <Route path='/news' element={<News />} />
            <Route path='/news/:uuid' element={<NewsDetail />} />
            <Route path='/contact' element={<Contact />} />
            <Route path='/careers' element={<Careers />} />
            <Route path='/downloads' element={<Downloads />} />
            <Route path='/credentials' element={<Credentials />} />
          </Route>

          {/* 管理员登录（无布局） */}
          <Route path='/admin/login' element={<S><LoginPage /></S>} />

          {/* 管理员页面（需登录） */}
          <Route element={<ProtectedRoute />}>
            <Route element={<S><AdminLayout /></S>}>
              <Route path='/admin' element={<Navigate to='/admin/dashboard' replace />} />
              <Route path='/admin/dashboard' element={<S><DashboardPage /></S>} />
              <Route path='/admin/contacts' element={<S><ContactsPage /></S>} />
              <Route path='/admin/news' element={<S><NewsListPage /></S>} />
              <Route path='/admin/news/new' element={<S><NewsFormPage /></S>} />
              <Route path='/admin/news/:uuid/edit' element={<S><NewsFormPage /></S>} />
              <Route path='/admin/users' element={<S><UsersPage /></S>} />
              <Route path='/admin/quotations' element={<S><QuotationsPage /></S>} />
              <Route path='/admin/careers' element={<S><AdminCareersPage /></S>} />
              <Route path='/admin/careers/new' element={<S><CareersFormPage /></S>} />
              <Route path='/admin/careers/:uuid/edit' element={<S><CareersFormPage /></S>} />
              <Route path='/admin/about' element={<S><AboutManagePage /></S>} />
              <Route path='/admin/about/summit/new' element={<S><SummitFormPage /></S>} />
              <Route path='/admin/about/summit/:id/edit' element={<S><SummitFormPage /></S>} />
              <Route path='/admin/database' element={<S><DatabasePage /></S>} />
            </Route>
          </Route>

          {/* 404 */}
          <Route path='*' element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
